/**
 * The escape hatch for a stuck service worker.
 *
 * Unregisters every service worker and deletes every Cache Storage entry, then
 * reloads. The next load fetches a completely fresh shell.
 *
 * Downloaded models are NOT touched: they live in IndexedDB, and `caches`
 * covers only the Cache API. Clearing the shell costs a few megabytes to
 * re-fetch; clearing the models would cost 300 MB and a lot of goodwill.
 *
 * "A few megabytes" undersells it and that matters: the precache includes the
 * 21 MB onnxruntime WASM binary, without which there is no inference at all.
 * This is the recovery path for a wedged service worker and nothing else — it
 * is never the way to apply a setting. Use `location.reload()` for that.
 */
export async function forceUpdate(): Promise<void> {
  // Doing this with no connection is a one-way trip. The shell it deletes is
  // the only copy — there is nothing to re-fetch it from — so the app does not
  // come back until it next sees a network. `navigator.onLine` is unreliable
  // for proving you are online, but false is trustworthy: the browser knows it
  // has no interface. Refuse on that rather than brick the app.
  if (!navigator.onLine) {
    throw new Error(
      'A fresh app download needs a connection: this deletes the offline copy of the app ' +
        'and re-fetches it, and with no network there is nothing to fetch. Reconnect first. ' +
        'Downloaded models are not affected either way.'
    );
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch {
    /* keep going — clearing caches alone is usually enough */
  }

  try {
    if ('caches' in globalThis) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* keep going — the reload below may still pick up a new shell */
  }

  location.reload();
}
